import React, { FC, useEffect, useMemo, useState } from "react";
import { useReactiveVar } from "@apollo/client";
import { useTranslation } from "react-i18next";

import DropdownMenu from "components/molecules/DropdownMenu";

import { authenticatedUserVar } from "store/authenticatedUser";
import { useSchools } from "contexts/SchoolsContext";
import useDebounce from "hooks/Debounce";
import { DropdownElement } from "types";
import { Title, CaretDown, DropdownWrapper } from "./styles";
import { Props } from "./types";

const SchoolDropdown: FC<Props> = ({
  visible,
  offsetTop,
  context,
  searchingFor,
}) => {
  const {
    allSchools,
    currentSchool,
    fetchSchools,
    setCurrentSchool,
  } = useSchools();
  const { t } = useTranslation();
  const authenticatedUser = useReactiveVar(authenticatedUserVar);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSchoolSearch = useDebounce(searchValue);

  useEffect(() => {
    const contextSearch = [];
    if (
      context === "student" &&
      !authenticatedUser?.authenticatedItem?.isAdmin
    ) {
      contextSearch.push({
        students: {
          some: {
            user: {
              id: {
                equals: authenticatedUser?.authenticatedItem?.id,
              },
            },
          },
        },
      });
    } else if (
      context === "teacher" &&
      !authenticatedUser?.authenticatedItem?.isAdmin
    ) {
      contextSearch.push({
        OR: [
          {
            teachers: {
              some: {
                user: {
                  id: {
                    equals: authenticatedUser?.authenticatedItem?.id,
                  },
                },
              },
            },
          },
          {
            schoolAdmins: {
              some: {
                id: {
                  equals: authenticatedUser?.authenticatedItem?.id,
                },
              },
            },
          },
        ],
      });
    }

    if (debouncedSchoolSearch) {
      const orSearch: any[] = [
        {
          name: {
            contains: debouncedSchoolSearch,
            mode: "insensitive",
          },
        },
        {
          code: {
            contains: debouncedSchoolSearch,
            mode: "insensitive",
          },
        },
      ];

      if (searchingFor === "students") {
        orSearch.push({
          students: {
            some: {
              user: {
                email: {
                  contains: debouncedSchoolSearch,
                  mode: "insensitive",
                },
              },
            },
          },
        });
      }

      if (searchingFor === "teachers") {
        orSearch.push({
          teachers: {
            some: {
              user: {
                email: {
                  contains: debouncedSchoolSearch,
                  mode: "insensitive",
                },
              },
            },
          },
        });
        orSearch.push({
          schoolAdmins: {
            some: {
              email: {
                contains: debouncedSchoolSearch,
                mode: "insensitive",
              },
            },
          },
        });
      }

      contextSearch.push({
        OR: orSearch,
      });
    }

    fetchSchools({
      variables: {
        where: {
          AND: contextSearch,
        },
      },
    });
  }, [
    debouncedSchoolSearch,
    fetchSchools,
    authenticatedUser,
    context,
    searchingFor,
  ]);

  const handleSelectSchool = (id: string, name: string) => {
    setCurrentSchool({ id, name });
  };

  const elements: DropdownElement[] = useMemo(() => {
    return (allSchools?.schools || []).map((singleSchool) => {
      return {
        name: singleSchool.name || "",
        onClick: () =>
          handleSelectSchool(singleSchool.id, singleSchool.name || ""),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSchools]);

  return (
    <>
      <Title>
        {currentSchool?.name || t("user.selectSchool")}
        <CaretDown />
      </Title>

      <DropdownWrapper>
        <DropdownMenu
          position="full"
          elements={elements}
          offsetTop={offsetTop || "6rem"}
          style={{ borderTop: "1px solid #ccc" }}
          visible={visible}
          filterable
          serverFilter
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />
      </DropdownWrapper>
    </>
  );
};

export default SchoolDropdown;
